import {useCallback, useState} from 'react';

import {LOGIN_TYPE_CODE, LoginTypeCode, OtpTypeCode} from '@/consts/loginTypes';

import loginConfigService from '../loginConfigService';

export const OTP_STEPS = {
    IDENTIFIER: 'IDENTIFIER',
    CODE: 'CODE',
} as const;

export type OtpStep = (typeof OTP_STEPS)[keyof typeof OTP_STEPS];

export interface UseLoginFlowReturn {
    currentMethod: LoginTypeCode;
    otpStep: OtpStep;
    otpChannel: OtpTypeCode;
    otpChannels: OtpTypeCode[];
    canSwitchMethod: boolean;
    hasOtpTabs: boolean;
    isOtpCodeStep: boolean;
    switchMethod: () => void;
    selectOtpChannel: (channel: OtpTypeCode) => void;
    goToOtpCodeStep: () => void;
    resetOtpFlow: () => void;
}

const useLoginFlow = (): UseLoginFlowReturn => {
    const {loginTypes, otpChannels} = loginConfigService.getLoginConfig();

    const [currentMethod, setCurrentMethod] = useState<LoginTypeCode>(loginTypes[0]);
    const [otpStep, setOtpStep] = useState<OtpStep>(OTP_STEPS.IDENTIFIER);
    const [otpChannel, setOtpChannel] = useState<OtpTypeCode>(otpChannels[0]);

    const canSwitchMethod = loginTypes.length > 1;
    const hasOtpTabs = otpChannels.length > 1;
    const isOtpCodeStep = currentMethod === LOGIN_TYPE_CODE.OTP && otpStep === OTP_STEPS.CODE;

    const switchMethod = useCallback((): void => {
        if (!canSwitchMethod) {
            return;
        }
        const currentIndex = loginTypes.indexOf(currentMethod);
        // Move to the next login type in the ordered list, wrapping back to the first when at the end.
        const nextIndex = (currentIndex + 1) % loginTypes.length;
        const nextMethod = loginTypes[nextIndex];

        setCurrentMethod(nextMethod);

        if (nextMethod === LOGIN_TYPE_CODE.OTP) {
            setOtpStep(OTP_STEPS.IDENTIFIER);
        }
    }, [canSwitchMethod, currentMethod, loginTypes]);

    const selectOtpChannel = useCallback(
        (channel: OtpTypeCode): void => {
            if (!otpChannels.includes(channel)) {
                return;
            }

            setOtpChannel(channel);
            setOtpStep(OTP_STEPS.IDENTIFIER);
        },
        [otpChannels]
    );

    const goToOtpCodeStep = useCallback((): void => {
        setOtpStep(OTP_STEPS.CODE);
    }, []);

    const resetOtpFlow = useCallback((): void => {
        setOtpStep(OTP_STEPS.IDENTIFIER);
    }, []);

    return {
        currentMethod,
        otpStep,
        otpChannel,
        otpChannels,
        canSwitchMethod,
        hasOtpTabs,
        isOtpCodeStep,
        switchMethod,
        selectOtpChannel,
        goToOtpCodeStep,
        resetOtpFlow,
    };
};

export default useLoginFlow;
